/**
 * Simplification de la trace par Ramer–Douglas–Peucker — EF-POI-009.
 *
 * La tolérance de 100 m est celle qui est ajoutée au corridor pour former le
 * rayon demandé à Overpass, conformément à EF-POI-002. Le filtrage final
 * s'applique ensuite sur la polyligne complète, jamais sur la simplifiée.
 */

import { projectOnSegment, type Coordinate } from './geodesy';
import { coordinateAt, progressionAt, type ComputedTrack } from './track';

/** Tolérance de simplification, en mètres. Valeur fixe de EF-POI-009. */
export const SIMPLIFY_TOLERANCE_METERS = 100;

export interface SimplifiedPoint {
  /** Indice du point dans la trace de calcul. */
  readonly index: number;
  readonly coordinate: Coordinate;
  readonly progressionMeters: number;
}

/**
 * Retourne les indices retenus, extrémités comprises, par ordre croissant.
 * Implémentation itérative : la profondeur de récursion serait proportionnelle
 * au nombre de points sur une trace défavorable.
 */
export function simplifyIndices(
  track: ComputedTrack,
  toleranceMeters: number = SIMPLIFY_TOLERANCE_METERS,
): readonly number[] {
  if (track.count === 0) return [];
  if (track.count <= 2) return Array.from({ length: track.count }, (_, i) => i);

  const keep = new Uint8Array(track.count);
  keep[0] = 1;
  keep[track.count - 1] = 1;

  const stack: Array<[number, number]> = [[0, track.count - 1]];
  while (stack.length > 0) {
    const frame = stack.pop();
    if (frame === undefined) break;
    const [first, last] = frame;
    if (last - first < 2) continue;

    const a = coordinateAt(track, first);
    const b = coordinateAt(track, last);

    let worstIndex = -1;
    let worstDistance = toleranceMeters;
    for (let i = first + 1; i < last; i += 1) {
      const d = projectOnSegment(coordinateAt(track, i), a, b).distance;
      if (d > worstDistance) {
        worstDistance = d;
        worstIndex = i;
      }
    }

    if (worstIndex < 0) continue;
    keep[worstIndex] = 1;
    stack.push([first, worstIndex], [worstIndex, last]);
  }

  const result: number[] = [];
  for (let i = 0; i < track.count; i += 1) {
    if (keep[i] === 1) result.push(i);
  }
  return result;
}

/** Simplifie la trace et retourne les points retenus avec leur progression. */
export function simplifyTrack(
  track: ComputedTrack,
  toleranceMeters: number = SIMPLIFY_TOLERANCE_METERS,
): readonly SimplifiedPoint[] {
  return simplifyIndices(track, toleranceMeters).map((index) => ({
    index,
    coordinate: coordinateAt(track, index),
    progressionMeters: progressionAt(track, index),
  }));
}
